import { Utils } from '@/common/Utils';
import { highlightKeyword } from '@/utilities';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { SyntheticEvent } from 'react';
import { COURSE_RECURRENCE } from '../../constants';
import { WithLocaleProps } from '../../providers/LocaleProvider';
import MoreVertIcon from '../../resources/images/icons-more-vertical.svg';
import { KLAYO_COLORS } from '../../themes';
import type { BaseTableRow, GridRowParams, TableProps } from '../../types/components/table.types';
import IntegrationAbbreviation from '../../views/settings/IntegrationAbbreviation';
import { Chip } from '../Chip';
import { SelectMenu } from '../SelectMenu';
import { EnrolmentStatusText, enrolmentStatusSortValue } from '../EnrolmentStatusText';
import type { CourseStudentsDialogType } from './CourseStudentsTable';
import { Table } from './Table';

export type StudentCoursesDialogType =
  | 'enrolled'
  | 'completed'
  | 'inProgress'
  | 'notStarted'
  | 'failed'
  | 'overdue'
  | 'pending';

export interface ManageTrainingTableRow extends BaseTableRow {
  /** Grid row key only. NOT the course SKU — a retaken course has several rows sharing one SKU, so
   *  callers that need the SKU (e.g. the certificate download) must read courseSourceId. */
  id: string;
  /** The course's catalog reference code (SKU). */
  courseSourceId?: string;
  courseId?: string | null;
  courseName?: string;
  competencies?: any[];
  competencyCount?: number;
  duration?: string;
  enrolledCount?: number;
  completedCount?: number;
  inProgressCount?: number;
  overdueCount?: number;
  recurrence?: number;
  status?: string;
  /** Past due and not passed. A MODIFIER on top of `status`, never a replacement for it — the
   *  status column renders the two as separate elements (see EnrolmentStatusText). */
  isOverdue?: boolean;
  progress?: number;
  dueDate?: string;
  courseAccessExpiresAt?: string;
  completedDate?: string;
  integrationSource?: number | null;
  /** On a course row, the course's provider. On a student's enrolment row (StudentCoursesDialog), the
   *  organisation's own LearnUpon portal holding that enrolment, null for OLC; sent back on unenrol. */
  integrationId?: string | null;
  competenciesLocked?: boolean;
  numberOfStars?: number;
  thumbnailUrl?: string;
  facultiesAsString?: string;
  learnUponEnrollmentId?: string;
  learnUponInstance?: number | null;
  courseType?: string;
  /** On a course row, the course's provider acronym, shown after the course name. */
  organizationIntegrationAbbreviation?: string;
  /** Internal CourseEnrollment id; drives the learning-history dialog. Null for non-local-cache rows. */
  courseEnrollmentId?: string;
  /** Internal EmployeeTraining id for manual-training rows. When set, the UI knows this is a
   *  manual training and uses the request activity feed in place of the enrolment history table. */
  employeeTrainingId?: string | null;
  isLearningPath?: boolean;
  /** LearningPathEnrollment id; used as the learning-history id in place of courseEnrollmentId. */
  learningPathEnrollmentId?: string | null;
  /** IntegrationLearnUponEnrollment id — only set on rows from the organisation's own LearnUpon
   *  portal. Used to fetch their learning history. */
  integrationEnrollmentId?: string | null;
  /** True when at least one issued certificate exists for this employee + course. Drives the
   *  "View Certificate" row action. */
  hasCertificate?: boolean;
  /** Server-computed: live LearnUpon enrolment, has a courseEnrollmentId, and not yet
   *  terminal-pass. Gates the "Set due date"/"Set expiry date" row actions. */
  canSetDates?: boolean;
}

interface ManageTrainingTableProps
  extends TableProps<ManageTrainingTableRow>, Partial<WithLocaleProps> {
  dialogType?: StudentCoursesDialogType;
  onRowAction?: (event: React.MouseEvent, row: ManageTrainingTableRow) => void;
  onCountBadgeClick?: (
    event: React.MouseEvent,
    row: ManageTrainingTableRow,
    type: CourseStudentsDialogType
  ) => void;
  onRecurrenceChange?: (
    row: ManageTrainingTableRow,
    recurrence: (typeof COURSE_RECURRENCE)[keyof typeof COURSE_RECURRENCE]
  ) => void;
  onRowSelect?: (event: SyntheticEvent, row: ManageTrainingTableRow) => void;
  onCompetencyClick?: (row: ManageTrainingTableRow) => void;
  showActions?: (row: ManageTrainingTableRow) => boolean;
  isRecurrenceLocked?: (row: ManageTrainingTableRow) => boolean;
  onRecurrenceLocked?: () => void;
}

export class ManageTrainingTable extends Table<ManageTrainingTableRow, ManageTrainingTableProps> {
  static columns = {
    courseName: {
      field: 'courseName',
      label: 'tableColumns.courseName',
    },
    competencies: {
      field: 'competencies',
      label: 'tableColumns.competencies',
    },
    duration: {
      field: 'duration',
      label: 'tableColumns.duration',
    },
    enrolled: {
      field: 'enrolled',
      label: 'tableColumns.enrolled',
    },
    completed: {
      field: 'completed',
      label: 'tableColumns.completed',
    },
    inProgress: {
      field: 'inProgress',
      label: 'tableColumns.inProgress',
    },
    overdue: {
      field: 'overdue',
      label: 'tableColumns.overdue',
    },
    recurrence: {
      field: 'recurrence',
      label: 'tableColumns.recurrence',
    },
    status: {
      field: 'status',
      label: 'manage.courseStudents.status',
    },
    progress: {
      field: 'progress',
      label: 'manage.courseStudents.progress',
    },
    dueDate: {
      field: 'dueDate',
      label: 'manage.courseStudents.dueDate',
    },
    expiry: {
      field: 'courseAccessExpiresAt',
      label: 'manage.studentCourses.expiry',
    },
    completedDate: {
      field: 'completedDate',
      label: 'manage.courseStudents.completedDate',
    },
    actions: {
      field: 'actions',
      label: 'tableColumns.actions',
    },
  };

  static defaultProps = {
    ...Table.defaultProps,
    columns: [
      { type: ManageTrainingTable.columns.courseName },
      { type: ManageTrainingTable.columns.competencies },
      { type: ManageTrainingTable.columns.duration },
      { type: ManageTrainingTable.columns.enrolled },
      { type: ManageTrainingTable.columns.completed },
      { type: ManageTrainingTable.columns.inProgress },
      { type: ManageTrainingTable.columns.overdue },
      { type: ManageTrainingTable.columns.recurrence },
      { type: ManageTrainingTable.columns.actions },
    ],
    noRowsMessage: 'No courses available',
    tree: false,
  };

  constructor(props: ManageTrainingTableProps) {
    super(props);

    this.state = {
      ...this.state,
      sortModel: this.props.sortModel
        ? this.props.sortModel
        : [{ field: 'courseName', sort: 'asc' }],
    };
  }

  getRowId(e: ManageTrainingTableRow): string {
    return this.tableId + '_' + e.id;
  }

  getTranslatedRecurrenceItems(): [
    string,
    { index: number; value: number; label: string; filterLabel: string },
  ][] {
    const { translate } = this.props;
    return Object.entries(COURSE_RECURRENCE).map(([key, item]) => [
      key,
      {
        ...item,
        label: translate?.(item.label) || item.label,
        filterLabel: translate?.(item.filterLabel) || item.filterLabel,
      },
    ]);
  }

  onRowClick(params: GridRowParams<ManageTrainingTableRow>, e: SyntheticEvent): void {
    const { onRowSelect } = this.props;
    if (onRowSelect) onRowSelect(e, params.row);
  }

  initColumns(): void {
    const {
      columns,
      sortable,
      onRowAction,
      onCountBadgeClick,
      onRecurrenceChange,
      dialogType,
      onCompetencyClick,
      keyword,
    } = this.props;

    this.columns = [];

    columns?.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === ManageTrainingTable.columns.courseName)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.courseName.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.courseName.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            flex: 1,
            minWidth: 200,
            valueGetter: (_value, row) => row.courseName,
            renderCell: (params) => (
              <>
                {highlightKeyword(params.row.courseName ?? '', keyword)}
                <IntegrationAbbreviation
                  data={params.row}
                  type={IntegrationAbbreviation.dataType.integrationUser.value}
                />
              </>
            ),
          });

        if (c.type === ManageTrainingTable.columns.competencies)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.competencies.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.competencies.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 130,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.competencyCount,
            renderCell: (params) =>
              params.row.competenciesLocked ? (
                (this.props.translate?.('manage.courseStudents.locked') ?? 'Locked')
              ) : Number(params.row.competencyCount) > 0 ? (
                <Chip
                  label={params.row.competencyCount}
                  onClick={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    onCompetencyClick?.(params.row);
                  }}
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
          });

        if (c.type === ManageTrainingTable.columns.duration)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.duration.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.duration.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 110,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.duration,
            renderCell: (params) => {
              const { translate } = this.props;
              if (!params.row.duration) return null;
              const count = Number(params.row.duration);
              const key = count > 1 ? 'tableColumns.durationHours' : 'tableColumns.durationHour';
              return (
                translate?.(key, { count: params.row.duration }) ??
                `${params.row.duration} ${count > 1 ? 'hours' : 'hour'}`
              );
            },
          });

        if (c.type === ManageTrainingTable.columns.enrolled)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.enrolled.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.enrolled.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 100,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.enrolledCount,
            renderCell: (params) =>
              (params.row.enrolledCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.enrolledCount}
                  onClick={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    onCountBadgeClick?.(e as any, params.row, 'enrolled');
                  }}
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
          });

        if (c.type === ManageTrainingTable.columns.completed)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.completed.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.completed.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 110,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.completedCount,
            renderCell: (params) =>
              (params.row.completedCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.completedCount}
                  onClick={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    onCountBadgeClick?.(e as any, params.row, 'completed');
                  }}
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
          });

        if (c.type === ManageTrainingTable.columns.inProgress)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.inProgress.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.inProgress.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 110,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.inProgressCount,
            renderCell: (params) =>
              (params.row.inProgressCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.inProgressCount}
                  onClick={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    onCountBadgeClick?.(e as any, params.row, 'inProgress');
                  }}
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
          });

        if (c.type === ManageTrainingTable.columns.overdue)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.overdue.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.overdue.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 100,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.overdueCount,
            renderCell: (params) =>
              (params.row.overdueCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.overdueCount}
                  onClick={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    onCountBadgeClick?.(e as any, params.row, 'overdue');
                  }}
                  textColor={KLAYO_COLORS.Red600}
                  backgroundColor={KLAYO_COLORS.Rose050}
                />
              ) : (
                0
              ),
          });

        if (c.type === ManageTrainingTable.columns.recurrence)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.recurrence.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.recurrence.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 160,
            headerAlign: 'right',
            align: 'right',
            valueGetter: (_value, row) => row.recurrence,
            renderCell: (params) => {
              if (params.row.recurrence == undefined) return null;
              const translatedItems = this.getTranslatedRecurrenceItems();
              const selectedItem =
                translatedItems.find(([, item]) => item.index === params.row.recurrence)?.[1] ??
                null;
              const isLocked = this.props.isRecurrenceLocked?.(params.row) ?? false;
              return (
                <Box sx={{ position: 'relative', width: '100%' }}>
                  <SelectMenu<(typeof COURSE_RECURRENCE)[keyof typeof COURSE_RECURRENCE]>
                    className='klayo-table_select'
                    menuClassName='Klayo_recurrence-menu-list'
                    clearable={false}
                    dense={true}
                    noSelectionError={true}
                    placeholder='Select'
                    value={selectedItem}
                    items={translatedItems}
                    onChange={(e) =>
                      onRecurrenceChange?.(
                        params.row,
                        e.target.value as (typeof COURSE_RECURRENCE)[keyof typeof COURSE_RECURRENCE]
                      )
                    }
                    sx={{ width: '100%', pointerEvents: isLocked ? 'none' : 'auto' }}
                  />
                  {isLocked && (
                    <Box
                      sx={{ position: 'absolute', inset: 0, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        this.props.onRecurrenceLocked?.();
                      }}
                    />
                  )}
                </Box>
              );
            },
          });

        if (c.type === ManageTrainingTable.columns.status)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.status.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.status.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 120,
            valueGetter: (_value, row) =>
              enrolmentStatusSortValue(row.status, row.isOverdue, this.props.translate),
            renderCell: (params) => (
              <EnrolmentStatusText
                status={params.row.status}
                isOverdue={params.row.isOverdue}
                translate={this.props.translate}
              />
            ),
          });

        if (c.type === ManageTrainingTable.columns.progress)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.progress.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.progress.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 160,
            valueGetter: (_value, row) => row.progress,
            renderCell: (params) => (
              <Stack direction='row' alignItems='center' gap={1} sx={{ width: '100%' }}>
                <LinearProgress
                  variant='determinate'
                  value={params.row.progress ?? 0}
                  sx={{
                    width: 80,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: KLAYO_COLORS.GreyGR100,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: '#4CAF50',
                    },
                  }}
                />
                <Typography variant='body2'>{params.row.progress ?? 0}%</Typography>
              </Stack>
            ),
          });

        if (c.type === ManageTrainingTable.columns.dueDate)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.dueDate.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.dueDate.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 130,
            valueGetter: (_value, row) => row.dueDate,
            renderCell: (params) => {
              const isPast = params.row.dueDate
                ? new Date(params.row.dueDate).setHours(0, 0, 0, 0) <
                  new Date().setHours(0, 0, 0, 0)
                : false;
              return (
                <Typography
                  variant='body2'
                  fontWeight={isPast ? 500 : 400}
                  color={isPast ? KLAYO_COLORS.TouchRedTR500 : KLAYO_COLORS.GreyGR900}
                >
                  {params.row.dueDate ? Utils.formatDate(params.row.dueDate) : ''}
                </Typography>
              );
            },
          });

        if (c.type === ManageTrainingTable.columns.expiry)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.expiry.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.expiry.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 130,
            valueGetter: (_value, row) => row.courseAccessExpiresAt,
            renderCell: (params) =>
              params.row.courseAccessExpiresAt ? (
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR900}>
                  {Utils.formatDate(params.row.courseAccessExpiresAt)}
                </Typography>
              ) : (
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR400}>
                  {this.props.translate?.('manage.courseStudents.notSet')}
                </Typography>
              ),
          });

        if (c.type === ManageTrainingTable.columns.completedDate)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.completedDate.field,
            headerName: this.props.translate?.(ManageTrainingTable.columns.completedDate.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 150,
            valueGetter: (_value, row) => row.completedDate,
            renderCell: (params) => {
              return (
                <Typography variant='body2' fontWeight={400} color={KLAYO_COLORS.GreyGR900}>
                  {params.row.completedDate ? Utils.formatDate(params.row.completedDate) : ''}
                </Typography>
              );
            },
          });

        if (c.type === ManageTrainingTable.columns.actions)
          this.columns.push({
            field: c.field || ManageTrainingTable.columns.actions.field,
            type: 'actions',
            filterable: false,
            align: 'right',
            width: 50,
            disableReorder: true,
            sortable: false,
            renderCell: (params) =>
              this.props.showActions?.(params.row)
                ? [
                    <GridActionsCellItem
                      key='action'
                      icon={<MoreVertIcon />}
                      label='Actions'
                      onClick={(e: any) => onRowAction?.(e, params.row)}
                    />,
                  ]
                : [],
          });
      }
    });
  }
}
